import { useEffect } from 'react'

interface TooltipHandlersProps {
  positionRef: React.RefObject<HTMLSpanElement>
  companyRef: React.RefObject<HTMLSpanElement>
  setShowPositionTooltip: React.Dispatch<React.SetStateAction<boolean>>
  setShowCompanyTooltip: React.Dispatch<React.SetStateAction<boolean>>
  setPositionTooltipPos: React.Dispatch<React.SetStateAction<{ top: number; left: number } | null>>
  setCompanyTooltipPos: React.Dispatch<React.SetStateAction<{ top: number; left: number } | null>>
  positionTooltipTimeoutRef: React.MutableRefObject<NodeJS.Timeout | null>
  companyTooltipTimeoutRef: React.MutableRefObject<NodeJS.Timeout | null>
}

export function useTooltipHandlers({
  positionRef,
  companyRef,
  setShowPositionTooltip,
  setShowCompanyTooltip,
  setPositionTooltipPos,
  setCompanyTooltipPos,
  positionTooltipTimeoutRef,
  companyTooltipTimeoutRef
}: TooltipHandlersProps) {
  // Helper function to clear tooltip timeouts
  const clearTooltipTimeouts = () => {
    if (positionTooltipTimeoutRef.current) {
      clearTimeout(positionTooltipTimeoutRef.current)
      positionTooltipTimeoutRef.current = null
    }
    if (companyTooltipTimeoutRef.current) {
      clearTimeout(companyTooltipTimeoutRef.current)
      companyTooltipTimeoutRef.current = null
    }
  }

  // Helper function to hide position tooltip
  const hidePositionTooltip = () => {
    setShowPositionTooltip(false)
    setPositionTooltipPos(null)
  }

  // Helper function to hide company tooltip
  const hideCompanyTooltip = () => {
    setShowCompanyTooltip(false)
    setCompanyTooltipPos(null)
  }

  // Helper function to hide all tooltips
  const hideAllTooltips = () => {
    clearTooltipTimeouts()
    hidePositionTooltip()
    hideCompanyTooltip()
  }

  // Helper function to check if click is outside position ref
  const isClickOutsidePositionRef = (target: Node | null): boolean => {
    return !!(positionRef.current && !positionRef.current.contains(target))
  }

  // Helper function to check if click is outside company ref
  const isClickOutsideCompanyRef = (target: Node | null): boolean => {
    return !!(companyRef.current && !companyRef.current.contains(target))
  }

  // Helper function to check if click is outside tooltip elements
  const isClickOutsideTooltips = (target: Node | null): boolean => {
    return isClickOutsidePositionRef(target) && isClickOutsideCompanyRef(target)
  }

  // Helper function to create scroll handler
  const createScrollHandler = () => {
    return () => hideAllTooltips()
  }

  // Helper function to create click outside handler
  const createClickOutsideHandler = () => (e: MouseEvent) => {
    if (!isClickOutsideTooltips(e.target as Node)) return
    hideAllTooltips()
  }

  // Helper function to create blur handler
  const createBlurHandler = () => {
    return () => hideAllTooltips()
  }

  // Helper function to add scroll listener
  const addScrollListener = (handleScroll: () => void) => {
    window.addEventListener('scroll', handleScroll, true)
  }

  // Helper function to add click outside listener
  const addClickOutsideListener = (handleClickOutside: (e: MouseEvent) => void) => {
    document.addEventListener('click', handleClickOutside)
  }

  // Helper function to add blur listener
  const addBlurListener = (handleBlur: () => void) => {
    window.addEventListener('blur', handleBlur)
  }

  // Helper function to add resize listener for tooltips
  const addResizeListenerForTooltips = () => {
    window.addEventListener('resize', hideAllTooltips)
  }

  // Helper function to setup tooltip event listeners
  const setupTooltipEventListeners = () => {
    const handleScroll = createScrollHandler()
    const handleClickOutside = createClickOutsideHandler()
    const handleBlur = createBlurHandler()

    addScrollListener(handleScroll)
    addClickOutsideListener(handleClickOutside)
    addBlurListener(handleBlur)
    addResizeListenerForTooltips()

    return { handleScroll, handleClickOutside, handleBlur }
  }

  // Helper function to cleanup tooltip event listeners
  const cleanupTooltipEventListeners = (handleScroll: () => void, handleClickOutside: (e: MouseEvent) => void, handleBlur: () => void) => {
    window.removeEventListener('scroll', handleScroll, true)
    document.removeEventListener('click', handleClickOutside)
    window.removeEventListener('blur', handleBlur)
    window.removeEventListener('resize', hideAllTooltips)
  }

  // Hide tooltips on scroll, blur, or click outside
  useEffect(() => {
    const { handleScroll, handleClickOutside, handleBlur } = setupTooltipEventListeners()

    return () => {
      cleanupTooltipEventListeners(handleScroll, handleClickOutside, handleBlur)
    }
  }, [])

  // Helper function to check if element is truncated
  const checkElementTruncation = (element: HTMLElement | null): boolean => {
    if (!element) return false
    return element.scrollWidth > element.clientWidth
  }

  // Helper function to get tooltip position from element
  const getTooltipPosition = (element: HTMLElement) => {
    const { top, left } = element.getBoundingClientRect()
    return { top: top - 8, left }
  }

  // Helper function to create ResizeObserver instance
  const createResizeObserver = (checkTruncation: () => void): ResizeObserver | null => {
    return typeof ResizeObserver !== 'undefined' ? new ResizeObserver(checkTruncation) : null
  }

  // Helper function to observe position ref
  const observePositionRef = (resizeObserver: ResizeObserver | null) => {
    if (!resizeObserver || !positionRef.current) return
    resizeObserver.observe(positionRef.current)
  }

  // Helper function to check if company ref can be observed
  const canObserveCompanyRef = (resizeObserver: ResizeObserver | null): boolean => {
    return !!(resizeObserver && companyRef.current)
  }

  // Helper function to observe company ref
  const observeCompanyRef = (resizeObserver: ResizeObserver | null) => {
    if (!canObserveCompanyRef(resizeObserver)) return
    resizeObserver!.observe(companyRef.current!)
  }

  // Helper function to observe both refs
  const observeBothRefs = (resizeObserver: ResizeObserver | null) => {
    observePositionRef(resizeObserver)
    observeCompanyRef(resizeObserver)
  }

  // Helper function to setup ResizeObserver
  const setupResizeObserver = (checkTruncation: () => void): ResizeObserver | null => {
    const resizeObserver = createResizeObserver(checkTruncation)
    observeBothRefs(resizeObserver)
    return resizeObserver
  }

  // Helper function to check if element exists and is truncated
  const isElementTruncated = (element: HTMLElement | null): boolean => {
    if (!element) return false
    return checkElementTruncation(element)
  }


  // Helper function to show tooltip if element is truncated
  const showTooltipIfTruncated = (
    ref: React.RefObject<HTMLSpanElement>,
    setShow: React.Dispatch<React.SetStateAction<boolean>>,
    setPos: React.Dispatch<React.SetStateAction<{ top: number; left: number } | null>>
  ) => {
    const element = ref.current
    if (!element) return
    if (!isElementTruncated(element)) return
    setPos(getTooltipPosition(element))
    setShow(true)
  }

  // Helper function to show tooltip after delay
  const showTooltipAfterDelay = (
    ref: React.RefObject<HTMLSpanElement>,
    setShow: React.Dispatch<React.SetStateAction<boolean>>,
    setPos: React.Dispatch<React.SetStateAction<{ top: number; left: number } | null>>,
    timeoutRef: React.MutableRefObject<NodeJS.Timeout | null>
  ) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
    setShow(false)
    setPos(null)
    timeoutRef.current = setTimeout(() => showTooltipIfTruncated(ref, setShow, setPos), 150)
  }

  // Helper function to handle tooltip mouse enter
  const handleTooltipMouseEnter = (
    ref: React.RefObject<HTMLSpanElement>,
    timeoutRef: React.MutableRefObject<NodeJS.Timeout | null>,
    setShow: React.Dispatch<React.SetStateAction<boolean>>,
    setPos: React.Dispatch<React.SetStateAction<{ top: number; left: number } | null>>
  ) => {
    showTooltipAfterDelay(ref, setShow, setPos, timeoutRef)
  }

  const createTooltipMouseEnterHandler = (
    ref: React.RefObject<HTMLSpanElement>,
    timeoutRef: React.MutableRefObject<NodeJS.Timeout | null>,
    setShow: React.Dispatch<React.SetStateAction<boolean>>,
    setPos: React.Dispatch<React.SetStateAction<{ top: number; left: number } | null>>
  ) => {
    const handler = () => handleTooltipMouseEnter(ref, timeoutRef, setShow, setPos)
    return handler
  }

  // Helper function to check if timeout exists
  const hasTimeout = (timeoutRef: React.MutableRefObject<NodeJS.Timeout | null>): boolean => {
    return !!timeoutRef.current
  }

  // Helper function to clear timeout
  const clearTimeoutRef = (timeoutRef: React.MutableRefObject<NodeJS.Timeout | null>) => {
    if (hasTimeout(timeoutRef)) {
      clearTimeout(timeoutRef.current!)
      timeoutRef.current = null
    }
  }

  // Helper function to hide tooltip
  const hideTooltip = (
    setShow: React.Dispatch<React.SetStateAction<boolean>>,
    setPos: React.Dispatch<React.SetStateAction<{ top: number; left: number } | null>>
  ) => {
    setShow(false)
    setPos(null)
  }

  // Helper function to cleanup tooltip on mouse leave
  const cleanupTooltipOnMouseLeave = (
    timeoutRef: React.MutableRefObject<NodeJS.Timeout | null>,
    setShow: React.Dispatch<React.SetStateAction<boolean>>,
    setPos: React.Dispatch<React.SetStateAction<{ top: number; left: number } | null>>
  ) => {
    clearTimeoutRef(timeoutRef)
    hideTooltip(setShow, setPos)
  }

  const createTooltipMouseLeaveHandler = (
    timeoutRef: React.MutableRefObject<NodeJS.Timeout | null>,
    setShow: React.Dispatch<React.SetStateAction<boolean>>,
    setPos: React.Dispatch<React.SetStateAction<{ top: number; left: number } | null>>
  ) => {
    return () => cleanupTooltipOnMouseLeave(timeoutRef, setShow, setPos)
  }

  // Helper function to get tooltip position from ref
  const getTooltipPositionFromRef = (ref: React.RefObject<HTMLSpanElement>): { top: number; left: number } | null => {
    if (!ref.current) return null
    return getTooltipPosition(ref.current)
  }

  const createTooltipMouseMoveHandler = (
    ref: React.RefObject<HTMLSpanElement>,
    show: boolean,
    setPos: React.Dispatch<React.SetStateAction<{ top: number; left: number } | null>>
  ) => {
    return () => {
      if (!show) return
      const position = getTooltipPositionFromRef(ref)
      if (position) setPos(position)
    }
  }

  // Helper function to create timeout with delay
  const createTimeout = (checkTruncation: () => void, delay: number): NodeJS.Timeout => {
    return setTimeout(checkTruncation, delay)
  }

  // Helper function to setup truncation check timeouts
  const setupTruncationTimeouts = (checkTruncation: () => void): NodeJS.Timeout[] => {
    return [
      createTimeout(checkTruncation, 100),
      createTimeout(checkTruncation, 500),
      createTimeout(checkTruncation, 1000)
    ]
  }

  // Helper function to cleanup truncation timeouts
  const cleanupTruncationTimeouts = (timeoutIds: NodeJS.Timeout[]) => {
    timeoutIds.forEach((timeoutId) => clearTimeout(timeoutId))
  }

  // Helper function to check truncation for both elements
  const checkTruncationForBothElements = () => {
    checkElementTruncation(positionRef.current)
    checkElementTruncation(companyRef.current)
  }

  // Helper function to add resize listener
  const addResizeListener = (checkTruncation: () => void) => {
    window.addEventListener('resize', checkTruncation)
  }

  // Helper function to setup truncation listeners
  const setupTruncationListeners = (checkTruncation: () => void) => {
    const timeoutIds = setupTruncationTimeouts(checkTruncation)
    const resizeObserver = setupResizeObserver(checkTruncation)
    addResizeListener(checkTruncation)
    return { timeoutIds, resizeObserver, checkTruncation }
  }

  // Helper function to cleanup resize observer
  const cleanupResizeObserver = (resizeObserver: ResizeObserver | null) => {
    if (!resizeObserver) return
    resizeObserver.disconnect()
  }

  // Helper function to cleanup truncation listeners
  const cleanupTruncationListeners = (timeoutIds: NodeJS.Timeout[], resizeObserver: ResizeObserver | null, checkTruncation: () => void) => {
    cleanupTruncationTimeouts(timeoutIds)
    window.removeEventListener('resize', checkTruncation)
    cleanupResizeObserver(resizeObserver)
  }

  return {
    hideAllTooltips,
    createTooltipMouseEnterHandler,
    createTooltipMouseLeaveHandler,
    createTooltipMouseMoveHandler,
    setupTruncationListeners,
    cleanupTruncationListeners,
    checkTruncationForBothElements
  }
}
